/**
 * useThemePreference Hook
 * 
 * Handles theme preference with system option support.
 * Wraps ThemeContext with additional 'system' mode handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { Platform, useColorScheme } from 'react-native';
import * as SecureStore from 'expo-secure-store';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme_preference';

async function getStoredPreference(): Promise<ThemePreference | null> {
  try {
    let stored: string | null = null;
    
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        stored = localStorage.getItem(STORAGE_KEY);
      }
    } else {
      stored = await SecureStore.getItemAsync(STORAGE_KEY);
    }
    
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // Silently fail
  }
  return null;
}

async function savePreference(preference: ThemePreference): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY, preference);
      }
    } else {
      await SecureStore.setItemAsync(STORAGE_KEY, preference);
    }
  } catch {
    // Silently fail
  }
}

interface UseThemePreferenceReturn {
  preference: ThemePreference;
  effectiveTheme: 'light' | 'dark';
  isLoading: boolean;
  setPreference: (preference: ThemePreference) => void;
}

export function useThemePreference(): UseThemePreferenceReturn {
  const [preference, setPreferenceState] = useState<ThemePreference>('light');
  const [isLoading, setIsLoading] = useState(true);
  const systemColorScheme = useColorScheme();

  // Load preference on mount
  useEffect(() => {
    async function loadPreference() {
      const stored = await getStoredPreference();
      if (stored) {
        setPreferenceState(stored);
      }
      setIsLoading(false);
    }
    loadPreference();
  }, []);

  const setPreference = useCallback((newPreference: ThemePreference) => {
    setPreferenceState(newPreference);
    savePreference(newPreference);
  }, []);

  // Compute effective theme based on preference and system settings
  const effectiveTheme: 'light' | 'dark' = 
    preference === 'system' 
      ? (systemColorScheme || 'light')
      : preference;

  return {
    preference,
    effectiveTheme,
    isLoading,
    setPreference,
  };
}

